import { storage } from '@abyss/web/tools/storage';
import { tokenizer } from '@abyss/web/tools/tokenizer';
import { SHA256, enc } from 'crypto-js';
import { flatten, intersection, uniq } from 'lodash';

import { APIConstants } from '../../../../../api/src/services/Constants';
import { convertObjectToUrlParams } from '../../../common/Utils';
import { ChipState } from '../../../store/useChipStore/chipStore';
import {
  Eligibility,
  UesVirtualCareKeywordMappingTable,
} from '../../../utils/providerSearch.utils';
import { getChipsByCoverages } from '../../../utils/psxHeader.utils';
import { handleSearchBarSuggestionClickOrEnter } from '../../../utils/typeahead.utils';
import {
  getCoverageTypes,
  getOnlineRetailers,
} from '../../../utils/user.utils';
import { adobeLinkTrackEvent } from '../../AdobeTagging/adobeLinkTrackEvent';
import {
  CareCategories,
  Constants,
  LOB,
  NULL_RESULTS,
  ReverseCoverageTypesCodes,
  UESSearchType,
} from '../../Constants';
import { ConstantsRoutes } from '../../ConstantsRoutes';
import { mappingSpecialityRollUpCodeToCategory } from '../../RecentActivity/helper';
import {
  CustomAttributesBlock,
  getNameForLinkTrack,
  getTypeAheadCategory,
} from '../../Utils/adobeTrackUtils/adobeTrackUtils';

const { GEO_LOCATION } = Constants.STORAGE_KEYS.SESSION;
export const MAX_NPI_LICENSE_AND_PHONE_NUMBER_RESULTS = 3;

export const createDividerTracker = (initialHasContent: boolean) => {
  let hasContent = initialHasContent;

  return (sectionIsVisible: boolean) => {
    const showDivider = hasContent;
    hasContent = hasContent || sectionIsVisible;
    return showDivider;
  };
};

export const getRenderableSectionItems = (entry, sortItems) =>
  entry?.items
    ?.filter((_, index) => {
      if (
        [
          UESSearchType.PhoneNumber,
          UESSearchType.NpiNumber,
          UESSearchType.LicenseNumber,
        ].includes(entry?.uesSearchType)
      ) {
        return index < MAX_NPI_LICENSE_AND_PHONE_NUMBER_RESULTS;
      }
      return true;
    })
    .sort(sortItems);

const getSuggestionSource = (
  search: string,
  psxKeyword: string,
  linkName: string,
  isIFPEarlyPay: boolean
) => {
  if (isIFPEarlyPay) {
    return 'earlypay-common-searches';
  }
  if (psxKeyword) {
    return 'common-searches';
  } else {
    const suffix = search === linkName ? 'suggestion' : linkName;
    return `auto-complete:${suffix}`;
  }
};

export const getGeoLocationFromStorage = () =>
  storage.local.get(GEO_LOCATION) || {};

interface GeoLocation {
  id?: string;
  name?: string;
  longitude?: string;
  latitude?: string;
  stateCode?: string;
  zipCode?: string;
  county?: string;
}

export const setGeoLocationToStorage = (newGeoLocation: GeoLocation) => {
  const currentGeoLocation = getGeoLocationFromStorage();
  // set the geo location to storage only if the new geo location is different from the current one
  // this to ensure that the geo location is updated only when it changes
  // prevent dispatching the event when the geo location is not changed.
  if (JSON.stringify(currentGeoLocation) !== JSON.stringify(newGeoLocation)) {
    storage.local.set(GEO_LOCATION, newGeoLocation);
    window.dispatchEvent(new Event('updateGeoLocationToStorage'));
  }
};

export const getLinkNameForAnalytics = (value) =>
  getNameForLinkTrack(
    value?.suggestion ||
      value?.displayName ||
      `common search:${value?.psxKeyword}`,
    value?.providerType
  );

export const getSearchMethodForAnalytics = (value) => {
  let searchMethod;
  if (value?.psxKeyword) {
    searchMethod = 'guided';
  } else {
    searchMethod = 'predictive';
  }
  return searchMethod;
};

export const handleCloseLocationOptionsList = (
  isShowingLocationDrawer: boolean | null,
  isShowingLocationDropdown: boolean | null,
  callbackSetCursor: (a: number) => void,
  callbackHandleCloseLocationDrawer: () => void,
  callbackHandleCloseLocationDropdown: () => void
) => {
  if (isShowingLocationDrawer) {
    callbackHandleCloseLocationDrawer();
    callbackSetCursor(-1);
  }
  if (isShowingLocationDropdown) {
    callbackHandleCloseLocationDropdown();
    callbackSetCursor(-1);
  }
};

export const setTypeAheadResults = (
  suggestions,
  providers,
  facilities,
  network: string,
  typeaheadSearchHeader
) =>
  suggestions
    ?.map((item) => ({ ...item, showSearchIcon: true }))
    .concat(
      network && (providers?.length || facilities?.length)
        ? {
            section: typeaheadSearchHeader,
            items: providers
              ?.concat(facilities)
              .map((item) => ({ ...item, showSearchIcon: false })),
          }
        : []
    );

export const setNullToken = (term, analyticsLinkName) =>
  tokenizer.create({
    originLinkNameForAnalytics: analyticsLinkName,
    search: term,
    searchType: '',
    searchMethod: 'typed',
    resultType: NULL_RESULTS,
  });

export const setStore = (
  {
    setDetailsStore,
    setTypeaheadSearchStore,
    setPCPSearchState,
    setAnalyticsState,
    setChipValue,
    setCoverageType,
    setSearchStore,
  },
  value
) => {
  setDetailsStore({
    providerId: value?.providerId,
    providerName: value?.displayName,
    searchByProvider: value.searchByProvider,
  });
  setTypeaheadSearchStore(
    {
      searchTerm: value?.searchTerm,
      searchMethod: value?.searchMethod,
      searchFromTypeAhead: value?.searchFromTypeAhead,
      typeaheadLinkName: value?.typeaheadLinkName,
      searchBySpeciality: value?.searchBySpeciality,
    },
    { replace: true }
  );
  setPCPSearchState({
    choosePCP: value?.choosePCP,
    dependentSeqNbr: value?.dependentSeqNbr,
  });
  setAnalyticsState({
    linkName: value?.linkName,
  });
  setChipValue(
    mappingSpecialityRollUpCodeToCategory(
      value?.speciality,
      value?.providerType
    )
  );
  setCoverageType(value?.coverageType);
  setSearchStore({
    onlineRetailers: getOnlineRetailers(value.isOnlineProviders),
  });
};

export const getTypeAheadStoreValues = (value, specialityPreferenceTable) => {
  const rollUpCodes =
    value?.pesKeyword ||
    value?.vertical?.code ||
    value?.includeSpecialityRollupCodes;

  const searchType =
    value?.searchType?.toLowerCase() || Constants.SEARCH_TYPES.SPECIALTY;

  const specialityPreferenceTuple = specialityPreferenceTable?.find((item) =>
    item.rollupCode
      ?.split(',')
      .every((val) => rollUpCodes?.split(',').includes(val))
  );

  const typeaheadStoreValues = {
    providerType: value?.providerType ?? '',
    search: value?.translatedPsxKeyword || value?.suggestion || value?.search,
    heading: value?.psxKeyword || value?.suggestion || value?.search,
    searchType,
    includeSpecialityRollupCodes: rollUpCodes,
    combinedIncludeSpecialityRollupCodes: rollUpCodes,
    pesKeyword: value?.pesKeyword,
    pseudoVerticals: value?.pseudoVerticals || '',
    preferredSpecialityCode: specialityPreferenceTuple
      ? specialityPreferenceTuple.specialityCode
      : '',
    searchBySpeciality: true,
  };

  const valueForTypeahead = {
    ...value,
    ...typeaheadStoreValues,
    keyword: value?.psxKeyword || value?.searchTerm || value?.userSearchTerm,
    category: value?.category,
    searchMethod: value?.searchMethod,
    searchTerm: value?.searchTerm,
    resultType:
      value?.resultType === NULL_RESULTS
        ? NULL_RESULTS
        : Constants.RESULT_SECTION.ALL,
    coverageTypes: uniq(value?.coverageTypes)?.join(','),
    platinumProgramCode:
      typeof value?.platinumProgramCode === 'string'
        ? value?.platinumProgramCode
        : undefined,
  };
  return valueForTypeahead;
};

export const updateStoreValues = (
  specialityPreferenceTable,
  value,
  {
    chipStore,
    setCoverageType,
    setTypeaheadSearchStore,
    setPCPSearchState,
    setAnalyticsState,
  }
) => {
  setAnalyticsState({
    originLinkNameForAnalytics: value?.linkName,
    linkName: value?.linkName,
  });
  const pcpStoreValues = {
    choosePCP: value?.choosePCP,
    pcpIndicator: false,
  };
  setPCPSearchState(pcpStoreValues);
  const chipStoreValues = {
    coverageType: value.coverageType || ReverseCoverageTypesCodes.MEDICAL,
  };
  setCoverageType(chipStoreValues.coverageType);

  const valueForTypeahead = getTypeAheadStoreValues(
    value,
    specialityPreferenceTable
  );
  setTypeaheadSearchStore(valueForTypeahead, { replace: true });
  const urlParams = convertObjectToUrlParams(
    { ...chipStore, ...chipStoreValues },
    valueForTypeahead
  );
  return { urlParams, valueForTypeahead };
};

export const navigateToMixedResultsPage = (
  specialityPreferenceTable,
  value,
  {
    chipStore,
    setCoverageType,
    setTypeaheadSearchStore,
    setPCPSearchState,
    setAnalyticsState,
  },
  navigate
) => {
  const { urlParams } = updateStoreValues(specialityPreferenceTable, value, {
    chipStore,
    setCoverageType,
    setTypeaheadSearchStore,
    setPCPSearchState,
    setAnalyticsState,
  });
  navigate(`${ConstantsRoutes.PROVIDER_SEARCH_RESULTS.path}${urlParams}`);
};

export const getReqCoverageType = (
  coverageType: ReverseCoverageTypesCodes[] | ReverseCoverageTypesCodes,
  getCoverageType: Array<string>
) => {
  if (Array.isArray(coverageType)) {
    const filteredCoverages = coverageType.filter(
      (coverage) =>
        !(
          coverage === ReverseCoverageTypesCodes.MEDICAL &&
          !getCoverageType?.includes(ReverseCoverageTypesCodes.MEDICAL)
        )
    );
    if (filteredCoverages.includes(ReverseCoverageTypesCodes.MEDICAL))
      return ReverseCoverageTypesCodes.MEDICAL;
    return filteredCoverages[0];
  }
  return coverageType;
};

export const getReqCoverageTypes = (
  coverageType: ReverseCoverageTypesCodes[] | ReverseCoverageTypesCodes,
  getCoverageType: Array<string>
) => {
  if (Array.isArray(coverageType)) {
    return coverageType.filter(
      (coverage) =>
        !(
          coverage === ReverseCoverageTypesCodes.MEDICAL &&
          !getCoverageType?.includes(ReverseCoverageTypesCodes.MEDICAL)
        )
    );
  }
  return coverageType;
};

export const handleSearchSelected = (
  enableRetriveTypeAheadData,
  specialityPreferenceTable,
  isIFPEarlyPay,
  value,
  store,
  navigate
) => {
  const updateValue = {
    ...value,
    choosePCP: false,
    dependentSeqNbr: value?.dependentSeqNbr,
    pseudoVerticals: value?.pseudoVerticals,
    isMixedSuggestions: value?.isMixedSuggestions || false,
    isUserTextSearch: value?.isUserTextSearch || false,
    searchFromTypeAhead: true,
  };
  // exisiting flow for mixed results page.
  if (!enableRetriveTypeAheadData) {
    navigateToMixedResultsPage(
      specialityPreferenceTable,
      updateValue,
      store,
      navigate
    );
    // we don't need to return anything as we are navigating to different page.
    return null;
    // enableRetriveTypeAheadData is turned on, navigate is provided for direct user search flow.
    // We need to pass only the independent variables to the url and navigate to the search results page.
  } else if (navigate) {
    const chipStoreValues = {
      coverageType: value.coverageType || ReverseCoverageTypesCodes.MEDICAL,
    };
    store.setCoverageType(chipStoreValues.coverageType);
    const search = value?.psxKeyword || value?.suggestion || value?.search;
    const searchTerm = value?.searchTerm;
    const { psxKeyword, linkName, platinumProgramCode, bhProgramFacilityAgg } =
      value;
    const urlParams = convertObjectToUrlParams({} as ChipState, {
      search,
      ...(searchTerm && searchTerm !== search ? { searchTerm } : {}),
      ...(isIFPEarlyPay ? { choosePCP: true } : {}),
      searchType: value?.searchType,
      searchSuggestionEN: value?.suggestion_en,
      [Constants.PSX_SOURCE]: getSuggestionSource(
        search,
        psxKeyword,
        linkName,
        isIFPEarlyPay
      ),
      active: value?.active,
      ...(platinumProgramCode && { platinumProgramCode }),
      ...(bhProgramFacilityAgg && { bhProgramFacilityAgg }),
    });
    navigate(
      `${
        isIFPEarlyPay
          ? ConstantsRoutes.GUEST_PROVIDER_SEARCH_RESULTS.path
          : ConstantsRoutes.PROVIDER_SEARCH_RESULTS.path
      }${urlParams}`
    );
    // we don't need to return anything as we are navigating to different page.
    return null;
  } else {
    // navigate is not provided for broswer back button flow where
    // navigation is not required and it returns the store values.
    const { valueForTypeahead: typeaheadValues } = updateStoreValues(
      specialityPreferenceTable,
      updateValue,
      store
    );
    return typeaheadValues;
  }
};

export const handleRandomSearch = (navigate, searchTerm, analyticsLinkName) => {
  navigate(
    `${ConstantsRoutes.NULL_SEARCH_RESULTS.path}/${setNullToken(
      searchTerm,
      analyticsLinkName
    )}`
  );
};

export const mapLocationType = (type) => {
  switch (type) {
    case APIConstants.PROVIDER_TYPES.providerGroup:
      return 'medical group';
    case APIConstants.PROVIDER_TYPES.practitioner:
      return 'provider';
    default:
      return 'facilities';
  }
};

export const getProviderResultTrackingData = (result) => {
  const { providerId, providerType, locationId, coverageType } = result;

  const mappedType = mapLocationType(providerType);

  if (locationId.endsWith('NoTin-T-NoAddress')) {
    return {
      providerType: mappedType,
      providerId,
    };
  }

  const locationSplit = locationId.split('-');
  const hashedTin = SHA256(locationSplit[1]).toString(enc.Hex);
  return {
    providerId,
    providerType: mappedType,
    tin: hashedTin,
    locationId: locationSplit[3],
    coverageType: coverageType?.at(0),
  };
};

export const getTypeAheadResultTrackingData = (TypeAheadData) => {
  const suggestions = TypeAheadData?.lang_provider?.langProvider || [];

  const providers: any = [];
  TypeAheadData?.organizations_uhc?.orgData?.map((org) =>
    providers.push(getProviderResultTrackingData(org))
  );
  TypeAheadData?.practitioners_uhc?.provData?.map((prac) =>
    providers.push(getProviderResultTrackingData(prac))
  );

  return {
    suggestions,
    providers,
  };
};

export const handleKeywordSuggestionKeyDownHelper = (
  ev: {
    key: string;
    preventDefault: () => void;
    target: { value: string };
  },
  specialityPreferenceTable,
  enableTypeAheadWildCardSearch,
  enableRetriveTypeAheadData,
  {
    keywordSearchTerm,
    commonSearchesData,
    isKeywordSearchLoading,
    searchButtonResults,
    setEnter,
    cursor,
    results,
    combineRollUpCodes,
    aggregatedAoeCodes,
    store,
    navigate,
    getTypeaheadData,
    headers,
    setCursor,
    convertTypeaheadProviderIdAndType,
    setIsFocusedOnKeywordSearchInput,
    searchInputOptionLocation,
    blurSearchInput,
    choosePCP,
    dependentSeqNbr,
    getCoverageType,
    bhProgramFacilityAgg,
    enableUESSuggestionMatch,
    currentMember,
    searchFromParams,
    isSuggestionClickOrEnter,
    isSearchButtonClickOrKeyboardEnter,
  }
) => {
  const minCursor = -1;
  const maxCursor =
    keywordSearchTerm?.trim().length < 2
      ? commonSearchesData.length + 1
      : searchButtonResults.length + 1;
  let value: any;

  if (['ArrowUp', 'ArrowDown'].some((key) => key === ev.key)) {
    ev.preventDefault();
  }
  const allowEnter = enableTypeAheadWildCardSearch || cursor !== minCursor;
  if (ev.key === 'Enter' && allowEnter && !isKeywordSearchLoading) {
    const analyticsLinkName = 'search enter';
    const typeaheadLinkName = 'typeahead search';
    const userSearchTerm = ev.target.value;
    setEnter(true);
    let customAttributesBlock;
    if (keywordSearchTerm?.length > 1) {
      if (cursor === -1) {
        const isUserOnSameResultsPage =
          isSearchButtonClickOrKeyboardEnter &&
          userSearchTerm &&
          searchFromParams &&
          userSearchTerm === searchFromParams;
        // when user is on search results page (wild card search) and press enter with same search term, we do not want to trigger the search again.
        if (isUserOnSameResultsPage) {
          return;
        }
        handleSearchBarSearchButtonClickOrEnter(
          {
            enableRetriveTypeAheadData,
            enableUESSuggestionMatch,
            typeAheadSuggestions: results || [],
            navigate,
            headers,
            analyticsLinkName,
            typeaheadLinkName,
            currentMember,
            userSearchTerm,
            combinedRollupCodes: combineRollUpCodes,
            aggregatedAoeCodes,
            bhProgramFacilityAgg,
            setIsFocusedOnKeywordSearchInput,
            convertTypeaheadProviderIdAndType,
            getTypeAheadCategory,
            store,
          },
          specialityPreferenceTable
        );
      } else {
        value = getTypeaheadData();
        const searchTerm =
          value?.psxKeyword || value?.suggestion || value?.search;
        const isUserOnSameResultsPage =
          isSuggestionClickOrEnter &&
          searchTerm &&
          searchFromParams &&
          searchTerm === searchFromParams;
        // when user is on search results (suggestion search) page and press enter on the same suggestion, we do not want to trigger the search again.
        if (isUserOnSameResultsPage) {
          return;
        }
        customAttributesBlock = value
          ? ({
              correlationId: headers?.correlationId,
              ...convertTypeaheadProviderIdAndType(value),
            } as CustomAttributesBlock)
          : undefined;
        setIsFocusedOnKeywordSearchInput(false);
        handleSearchBarSuggestionClickOrEnter({
          enableRetriveTypeAheadData,
          specialityPreferenceTable,
          value,
          searchTerm: keywordSearchTerm,
          currentMember,
          getCoverageType,
          dependentSeqNbr,
          choosePCP,
          ...store,
          navigate,
        });
      }
    }
    adobeLinkTrackEvent({
      name: analyticsLinkName,
      location: `body:${searchInputOptionLocation}`,
      type: 'internal',
      customAttributesBlock,
    });
    blurSearchInput();
  } else if (ev.key === 'ArrowUp' && cursor > minCursor) {
    setCursor((previous) => previous - 1);
  } else if (ev.key === 'ArrowDown' && cursor < maxCursor) {
    setCursor((previous) => previous + 1);
  }
};

export const handleSearchBarSearchButtonClickOrEnter = (
  {
    isLLMGeneralSearchEnabled = false,
    enableUESSuggestionMatch,
    enableRetriveTypeAheadData,
    typeAheadSuggestions,
    navigate,
    headers,
    analyticsLinkName,
    typeaheadLinkName,
    currentMember,
    userSearchTerm,
    combinedRollupCodes,
    aggregatedAoeCodes,
    bhProgramFacilityAgg,
    setIsFocusedOnKeywordSearchInput,
    convertTypeaheadProviderIdAndType,
    getTypeAheadCategory,
    store,
  },
  specialityPreferenceTable
) => {
  const isMixedSuggestions =
    typeAheadSuggestions.some((suggest) => suggest.suggestion) &&
    typeAheadSuggestions.some((suggest) => suggest.section);

  let isMatchingAutoSuggestion;
  if (enableUESSuggestionMatch) {
    isMatchingAutoSuggestion = typeAheadSuggestions.find((suggestion) =>
      suggestion.suggestion
        ? (suggestion?.suggestion).toLowerCase() ===
          userSearchTerm.toLowerCase()
        : false
    );
  }

  let searchType;
  let isUserTextSearch = false;

  if (isLLMGeneralSearchEnabled) {
    searchType = Constants.SEARCH_TYPES.SPECIALTY;
    isUserTextSearch = true;
  } else {
    if (typeAheadSuggestions.length > 0) {
      const isOnlyNamed = typeAheadSuggestions.every(
        (suggest) => suggest.section
      );
      const isOnlySpecialty = typeAheadSuggestions.every(
        (suggest) => suggest.suggestion
      );

      if (isOnlyNamed || isMixedSuggestions) {
        searchType = Constants.SEARCH_TYPES.NAME;
      }

      if (isOnlySpecialty) {
        searchType = Constants.SEARCH_TYPES.SPECIALTY;
      }
    } else {
      searchType = Constants.SEARCH_TYPES.SPECIALTY;
      isUserTextSearch = true;
    }
  }
  const isIFPEarlyPay = !!currentMember?.isIFPEarlyPay;
  const combinedCoverageTypes = flatten(
    typeAheadSuggestions
      .filter((suggest) => suggest.suggestion)
      .map((suggest) => [...suggest.coverageType])
  );

  const memberCoverages = getCoverageTypes(currentMember);
  const finalCoverageTypes = combinedCoverageTypes.length
    ? intersection(combinedCoverageTypes, memberCoverages)
    : memberCoverages;
  let customAttributesBlock;
  if (userSearchTerm?.length > 1) {
    customAttributesBlock = userSearchTerm
      ? ({
          correlationId: headers?.correlationId,
          ...convertTypeaheadProviderIdAndType(userSearchTerm),
        } as CustomAttributesBlock)
      : undefined;
    setIsFocusedOnKeywordSearchInput(false);
    const typeaheadValues = handleSearchSelected(
      enableRetriveTypeAheadData,
      specialityPreferenceTable,
      isIFPEarlyPay,
      {
        userSearchTerm,
        linkName: analyticsLinkName,
        typeaheadLinkName,
        searchMethod: 'typed',
        resultType: userSearchTerm
          ? Constants.RESULT_SECTION.ALL
          : NULL_RESULTS,
        search: userSearchTerm,
        searchTerm: userSearchTerm,
        heading: userSearchTerm,
        isMixedSuggestions,
        searchType,
        includeSpecialityRollupCodes: isLLMGeneralSearchEnabled
          ? null
          : isMatchingAutoSuggestion
          ? isMatchingAutoSuggestion.vertical
          : combinedRollupCodes,
        aoeCodes: isMatchingAutoSuggestion
          ? isMatchingAutoSuggestion.aoeCodes
          : aggregatedAoeCodes,
        coverageTypes: finalCoverageTypes,
        coverageType:
          memberCoverages.length === 0 || memberCoverages.includes('M')
            ? 'M'
            : memberCoverages?.[0],
        isUserTextSearch,
        bhProgramFacilityAgg: isMatchingAutoSuggestion
          ? isMatchingAutoSuggestion.bhProgramFacility
          : bhProgramFacilityAgg,
        platinumProgramCode: isMatchingAutoSuggestion?.platinumProgramCode,
        active: isMatchingAutoSuggestion
          ? isMatchingAutoSuggestion.active
          : true,
      },
      store,
      navigate
    );
    return typeaheadValues;
  }
  let analyticsLocationName = 'search-input-option-list';
  let analyticsSearchBlock;
  if (userSearchTerm) {
    analyticsLocationName += `:${getTypeAheadCategory(userSearchTerm)}`;
    analyticsSearchBlock = {
      linkPosition: '1',
    };
  }
  adobeLinkTrackEvent({
    name: analyticsLinkName,
    location: analyticsLocationName,
    type: 'internal',
    destinationUrl: '',
    ...(analyticsSearchBlock && { searchBlock: analyticsSearchBlock }),
    customAttributesBlock,
  });
  return null;
};
export const isVirtualVisitChipEnabled = (
  virtualVisitChipEnabled,
  showVirtualCare,
  isPreEffective,
  suppressVirtualCareQuickCare
) =>
  virtualVisitChipEnabled &&
  showVirtualCare &&
  !isPreEffective &&
  !suppressVirtualCareQuickCare;

export const getFilteredChips = (
  currentMember,
  isMixedResultsPage,
  categoryCount,
  visionChipEnabled,
  dentalChipEnabled,
  isVirtualCareSuppressedForEmpire,
  enableVirtualVisitChip,
  eligibilities = [] as Eligibility[],
  enableVirtualCareEpic = false
) => {
  const chips = getChipsByCoverages(
    Constants.MOCK_CARE_CATEGORIES,
    currentMember?.eligibility,
    currentMember,
    isVirtualCareSuppressedForEmpire,
    enableVirtualVisitChip,
    eligibilities,
    enableVirtualCareEpic
  );

  const enabledChips = chips.map((chip) => {
    const enabled = !isMixedResultsPage
      ? true
      : !!categoryCount.find(
          (item) => item.chipCategory === chip.id && item.chipEnabled
        );
    return { ...chip, enabled };
  });

  const filteredChips = enabledChips.filter((chip) => {
    if (chip.value === CareCategories.VISION && visionChipEnabled === false) {
      return false;
    }
    if (chip.value === CareCategories.DENTAL && dentalChipEnabled === false) {
      return false;
    }
    if (isMixedResultsPage && chip.value === CareCategories.ALL) {
      return false;
    }
    return true;
  });

  return filteredChips;
};

export const validateChipsToTabEnabled = (
  chipsToTabsEnabled,
  isMixedResultsPage
) => {
  let check;
  if (chipsToTabsEnabled && isMixedResultsPage) {
    check = true;
  }
  if (chipsToTabsEnabled && !isMixedResultsPage) {
    check = false;
  }
  if (!chipsToTabsEnabled && !isMixedResultsPage) {
    check = true;
  }
  if (!chipsToTabsEnabled && isMixedResultsPage) {
    check = true;
  }
  return check;
};

export const checkNonUhcMnrMember = (obapiMembers) =>
  obapiMembers.some((obapiMember) =>
    obapiMember?.lineOfBusiness === 'M&R'
      ? obapiMember?.networks?.some(
          (network) => network?.type === 'VISION' && network.value.length === 0
        )
      : false
  );

export const isMNRHbpbCodePresent = (member, hbpbCodes) =>
  member?.lineOfBusiness === LOB.MNR &&
  member?.eligibility.some((memberEligibility) =>
    memberEligibility?.memberHealthCoverage?.coverageType.some((coverage) =>
      hbpbCodes.includes(coverage?.productCode)
    )
  );

export const checkVisionCoverageTypeAhead = (typeAheadSuggestions) =>
  typeAheadSuggestions.some((typeAheadSuggestion) =>
    typeAheadSuggestion?.coverageType?.includes('V')
  );

export const getMatchingVirtualCareEntries = ({
  uesVirtualCareKeywordMappingTable = [],
  searchTerm = '',
  vertical = [],
  enablePseudoRollupCodeOnlyMatch = '',
}: {
  uesVirtualCareKeywordMappingTable: UesVirtualCareKeywordMappingTable[];
  searchTerm: string;
  vertical: string[];
  enablePseudoRollupCodeOnlyMatch: string;
}) => {
  const lowerSearch = searchTerm?.toLowerCase();

  return uesVirtualCareKeywordMappingTable?.filter((entry) =>
    enablePseudoRollupCodeOnlyMatch === 'Y'
      ? vertical?.includes(entry?.pseudoRollupCode)
      : entry?.keyword
          ?.toLowerCase()
          .split(',')
          .map((k) => k.trim())
          .includes(lowerSearch) && vertical?.includes(entry?.pseudoRollupCode)
  );
};

const normalizeForCompare = (value = '') =>
  value.toLowerCase().replaceAll(' ', '');

const getProviderAlternateNames = (
  providerName: string,
  vcAlternateNames: Record<string, string[]>
) => {
  const normalizedProviderName = providerName.toLowerCase();
  return [
    ...(vcAlternateNames[providerName] ||
      vcAlternateNames[normalizedProviderName] ||
      []),
    providerName,
  ];
};

const isProviderNameInSection = (
  providerName: string,
  sectionItems: string[] = []
) => {
  const normalizedProviderName = providerName.toLowerCase();
  return sectionItems?.some(
    (name) => name?.toLowerCase() === normalizedProviderName
  );
};

const isVirtualCareNameMatch = ({
  providerName,
  vcAlternateNames,
  enableVirtualCareNameSearchPsxLogic,
  normalizedKeywordSearchTerm,
  sectionItems,
}: {
  providerName: string;
  vcAlternateNames: Record<string, string[]>;
  enableVirtualCareNameSearchPsxLogic: string;
  normalizedKeywordSearchTerm: string;
  sectionItems: string[];
}) => {
  const alternateNames = getProviderAlternateNames(
    providerName,
    vcAlternateNames
  );
  return (
    (enableVirtualCareNameSearchPsxLogic === 'Y' &&
      alternateNames.some((altName) =>
        normalizeForCompare(altName).startsWith(normalizedKeywordSearchTerm)
      )) ||
    isProviderNameInSection(providerName, sectionItems)
  );
};

export const getVirtualCareProvidersForTypeaheadFromEligibilities = ({
  suggestion,
  uesVirtualCareKeywordMappingTable,
  eligibilities,
  enablePseudoRollupCodeOnlyMatch,
  enableVirtualCareNameSearchPsxLogic,
  keywordSearchTerm = '',
  virtualCareNameSearchAlternatives = '{}',
}: {
  suggestion: any[];
  uesVirtualCareKeywordMappingTable: UesVirtualCareKeywordMappingTable[];
  eligibilities: Eligibility[];
  enablePseudoRollupCodeOnlyMatch: string;
  enableVirtualCareNameSearchPsxLogic: string;
  keywordSearchTerm: string;
  virtualCareNameSearchAlternatives: string;
}): Array<{ careGroup: string; serviceType: string; providers: any[] }> => {
  if (!suggestion || !uesVirtualCareKeywordMappingTable || !eligibilities)
    return [];

  const results: Array<{
    careGroup: string;
    serviceType: string;
    providers: any[];
  }> = [];
  for (const item of suggestion) {
    const matchingEntries = getMatchingVirtualCareEntries({
      uesVirtualCareKeywordMappingTable,
      searchTerm: item.suggestion,
      vertical: item.vertical?.split(',') || [],
      enablePseudoRollupCodeOnlyMatch,
    });

    for (const entry of matchingEntries) {
      const found = eligibilities?.find(
        (el) => el?.serviceType === entry?.serviceType
      );
      if (found) {
        results.push({
          careGroup: found.careGroup,
          serviceType: found.serviceType,
          providers: found.providers,
        });
      }
    }
  }

  const section = suggestion?.find((item) => item?.heading);
  if (section || enableVirtualCareNameSearchPsxLogic === 'Y') {
    let vcAlternateNames: Record<string, string[]> = {};
    try {
      vcAlternateNames = JSON.parse(virtualCareNameSearchAlternatives) || {};
    } catch {
      vcAlternateNames = {};
    }
    const normalizedKeywordSearchTerm = normalizeForCompare(keywordSearchTerm);
    const sectionItems = (section?.items || []) as string[];
    eligibilities?.forEach((eligibility) => {
      if (
        Array.isArray(eligibility?.providers) &&
        eligibility?.providers?.some((prov) => {
          const providerName = prov?.providerName || '';
          return isVirtualCareNameMatch({
            providerName,
            vcAlternateNames,
            enableVirtualCareNameSearchPsxLogic,
            normalizedKeywordSearchTerm,
            sectionItems,
          });
        })
      ) {
        results.push({
          careGroup: eligibility?.careGroup,
          serviceType: eligibility?.serviceType,
          providers: eligibility?.providers.filter((prov) => {
            const providerName = prov?.providerName || '';
            return isVirtualCareNameMatch({
              providerName,
              vcAlternateNames,
              enableVirtualCareNameSearchPsxLogic,
              normalizedKeywordSearchTerm,
              sectionItems,
            });
          }),
        });
      }
    });
  }
  return results;
};

export const getUniqueVirtualCareProviders = (virtualCareProviders) => {
  const allVirtualCareProviders = virtualCareProviders?.flatMap((obj) =>
    obj?.providers?.map((prov) => ({
      ...prov,
      careGroup: obj?.careGroup,
      serviceType: obj?.serviceType,
    }))
  );
  const providerMap = new Map();
  allVirtualCareProviders?.forEach((prov) => {
    const key = prov?.providerName;
    if (!providerMap?.has(key)) {
      providerMap?.set(key, {
        ...prov,
        serviceTypes: [prov?.serviceType],
      });
    } else {
      const existing = providerMap?.get(key);
      if (!existing?.serviceTypes.includes(prov?.serviceType)) {
        existing?.serviceTypes.push(prov?.serviceType);
      }
    }
  });

  // Convert map to array and join serviceTypes
  return Array.from(providerMap?.values()).map((prov) => ({
    ...prov,
    serviceType: prov?.serviceTypes.join(', '),
  }));
};

export const getMpCodes = (
  specialityRollupCodes: string,
  programPromotionsRollupCodesTable
): string[] => {
  if (!programPromotionsRollupCodesTable || !specialityRollupCodes) return [];

  const rollupCodes = new Set(
    specialityRollupCodes?.split(',').map((code) => code.trim())
  );

  const mpCodes = new Set<string>(
    programPromotionsRollupCodesTable
      .filter((entry) => rollupCodes?.has(entry?.psuedoRollupCode))
      .map((entry) => entry?.mpcode)
  );

  return Array.from(mpCodes);
};

export interface CeOffersData {
  placementIdentifier: string;
  offerType: string;
  offerDescription: string;
  dismissText: string;
  source: string;
  cta: {
    afterLinkIcon: string;
    ctaText: string;
    ctaType: string;
    ctaValue: string;
    dataUiElementName: string;
    dataUiSectionName: string;
    openNewWindow: boolean;
  };
  logo: string;
  offerId: string;
  offerName: string;
  offerToken: string;
  MPCode: string;
}

export const getProgramPromotionsForTypeahead = (
  ceOffersData,
  mpCodes
): Array<CeOffersData> => {
  if (!ceOffersData || !mpCodes || mpCodes.length === 0) {
    return [];
  }

  return ceOffersData.filter((offer) => {
    const offerMPCode = offer?.MPCode;
    return mpCodes.some((mpCode) => offerMPCode?.includes(mpCode));
  });
};
